import { useCallback, useEffect, useRef, useState } from 'react';
import { youtubeChannel, type YoutubeCommand, type YoutubeState } from '../lib/youtube';

/**
 * Drives the player tab from the studio.
 *
 * The transport buttons act here and the position comes back the same way, so
 * the studio never has to guess where the clip is - the player is the
 * authority, exactly as the video element is for a file.
 *
 * Keyed per player, so several clips can be lined up before a show and cut
 * between - which is the whole point of preparing them in advance. A null key
 * means this source is not a YouTube one and no channel is opened at all.
 */
export function useYoutubeControl(key: string | null) {
  const [state, setState] = useState<YoutubeState | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!key) return;
    const open = new BroadcastChannel(youtubeChannel(key));
    channel.current = open;

    open.onmessage = (event: MessageEvent<YoutubeState | YoutubeCommand>) => {
      const incoming = event.data;
      if (incoming.type !== 'state') return;

      setState((previous) =>
        // A report from before YouTube knows the duration carries zeroes, and
        // taking them made the scrubber jump to 0:00 and back several times a
        // second. The last known position survives until a real one arrives.
        incoming.ready || !previous
          ? incoming
          : { ...incoming, position: previous.position, duration: previous.duration },
      );
    };

    // The player may have been opened before this mounted - after a studio
    // reload, say - so it is asked to say where it is rather than waited for.
    open.postMessage({ type: 'hello' } satisfies YoutubeCommand);

    return () => {
      open.close();
      channel.current = null;
    };
  }, [key]);

  const send = useCallback((command: YoutubeCommand) => {
    channel.current?.postMessage(command);
  }, []);

  return { state, send };
}
